import { useQuery, useMutation, useQueryClient } from 'react-query';
import SampleTagsAPI, {
  SampleTagListParams,
  SampleTagListResponse,
  SampleTagCreateData,
} from '../Api/sample-tags';
import { SampleTagResponse } from '../../Components/SampleTags/AddTags/types';

export const SAMPLE_TAGS_QUERY_KEYS = {
  all: ['sample-tags'] as const,
  lists: () => [...SAMPLE_TAGS_QUERY_KEYS.all, 'list'] as const,
  list: (userId: string, params?: SampleTagListParams) =>
    [...SAMPLE_TAGS_QUERY_KEYS.lists(), userId, params] as const,
  details: () => [...SAMPLE_TAGS_QUERY_KEYS.all, 'detail'] as const,
  detail: (userId: string, tagId: string) =>
    [...SAMPLE_TAGS_QUERY_KEYS.details(), userId, tagId] as const,
};

/**
 * Hook to fetch list of sample tags
 */
export const useSampleTagsList = (
  userId: string,
  params?: SampleTagListParams,
  options?: {
    enabled?: boolean;
    onSuccess?: (data: SampleTagListResponse) => void;
    onError?: (error: Error) => void;
  },
) =>
  useQuery(
    SAMPLE_TAGS_QUERY_KEYS.list(userId, params),
    () => SampleTagsAPI.list(userId, params),
    {
      enabled: !!userId && options?.enabled !== false,
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    },
  );

/**
 * Hook to fetch list of sample tags with signed URLs included
 */
export const useSampleTagsListWithSignedUrls = (
  userId: string,
  params?: Omit<SampleTagListParams, 'include_signed_urls'>,
  options?: {
    enabled?: boolean;
    onSuccess?: (data: SampleTagListResponse) => void;
    onError?: (error: Error) => void;
  },
) =>
  useQuery(
    SAMPLE_TAGS_QUERY_KEYS.list(userId, {
      ...params,
      include_signed_urls: true,
    }),
    () => SampleTagsAPI.list(userId, { ...params, include_signed_urls: true }),
    {
      enabled: !!userId && options?.enabled !== false,
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    },
  );

/**
 * Hook to fetch a single sample tag's full details
 */
export const useSampleTagDetail = (
  userId: string,
  tagId: string,
  options?: {
    enabled?: boolean;
    onSuccess?: (data: SampleTagResponse) => void;
    onError?: (error: Error) => void;
  },
) =>
  useQuery(
    SAMPLE_TAGS_QUERY_KEYS.detail(userId, tagId),
    () => SampleTagsAPI.getById(userId, tagId),
    {
      enabled: !!userId && !!tagId && options?.enabled !== false,
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    },
  );

/**
 * Hook to create a sample tag
 */
export const useSampleTagCreate = (
  userId: string,
  options?: {
    onSuccess?: (data: SampleTagResponse) => void;
    onError?: (error: Error) => void;
  },
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (tagData: SampleTagCreateData) => SampleTagsAPI.create(userId, tagData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(SAMPLE_TAGS_QUERY_KEYS.lists());
        options?.onSuccess?.(data);
      },
      onError: options?.onError,
    },
  );
};

/**
 * Hook to update a sample tag
 */
export const useSampleTagUpdate = (
  userId: string,
  options?: {
    onSuccess?: (data: SampleTagResponse) => void;
    onError?: (error: Error) => void;
  },
) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      tagId,
      updates,
    }: {
      tagId: string;
      updates: Partial<SampleTagCreateData>;
    }) => SampleTagsAPI.update(userId, tagId, updates),
    {
      onSuccess: (data: SampleTagResponse, variables) => {
        queryClient.invalidateQueries(SAMPLE_TAGS_QUERY_KEYS.lists());
        queryClient.invalidateQueries(
          SAMPLE_TAGS_QUERY_KEYS.detail(userId, variables.tagId),
        );
        options?.onSuccess?.(data);
      },
      onError: options?.onError,
    },
  );
};

/**
 * Hook to get a signed URL for a sample tag photo
 * @param userId - User ID
 * @param fileKey - The file key (UUID-based filename)
 * @param enabled - Whether to fetch the signed URL
 * @returns Query result with signed URL data
 */
export const useSignedImageUrl = (
  userId: string,
  fileKey: string | undefined,
  enabled = true,
) =>
  useQuery(
    ['sample-tag-signed-url', userId, fileKey],
    () => {
      if (!fileKey) {
        throw new Error('File key is required for generating signed URL');
      }
      return SampleTagsAPI.generatePhotoReadUrl(userId, fileKey);
    },
    {
      enabled: enabled && !!userId && !!fileKey,
      staleTime: 30 * 60 * 1000,
      cacheTime: 35 * 60 * 1000,
      retry: false,
    },
  );
